import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { Task } from '../../../../types/task';
import { isDarkAtom } from '../../../../atoms/shared/uiAtoms';
import { TaskItemV2 } from './TaskItemV2';
import { QuickAddBarV2 } from '../projects/QuickAddBarV2';

interface RecentTimeGroupProps {
  groupKey: 'yesterday' | 'today' | 'tomorrow';
  tasks: Task[];
  onTaskClick: (taskId: string) => void;
  onTaskToggle: (taskId: string, completed: boolean) => void;
  onTaskUpdate?: (taskId: string, updates: { projectId?: string; vital?: boolean; urgent?: boolean }) => void;
  onDateJump: (date: Date) => void; // 跳转到具体日期
  onTaskCreated?: () => void;
}

/**
 * Recent模式的时间分组组件
 * 类似ProjectV2 expanded模式的展示方式
 */
const RecentTimeGroupComponent: React.FC<RecentTimeGroupProps> = ({
  groupKey,
  tasks,
  onTaskClick,
  onTaskToggle,
  onTaskUpdate,
  onDateJump,
  onTaskCreated
}) => {
  const { t } = useTranslation(['v3/tasks']);
  const [isDark] = useAtom(isDarkAtom);
  const [showQuickAdd, setShowQuickAdd] = useState(false);

  // 计算日期
  const targetDate = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch (groupKey) {
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return yesterday;
      case 'today':
        return today;
      case 'tomorrow':
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow;
      default:
        return today;
    }
  }, [groupKey]);

  // 计算完成百分比
  const completionStats = useMemo(() => {
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter(task => task.done).length;
    const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    
    return {
      total: totalTasks,
      completed: completedTasks,
      rate: completionRate
    };
  }, [tasks]);

  // 获取分组标题
  const getGroupTitle = () => {
    switch (groupKey) {
      case 'yesterday':
        return t('dateSelector.yesterday');
      case 'today':
        return t('dateSelector.today');
      case 'tomorrow':
        return t('dateSelector.tomorrow');
      default:
        return groupKey;
    }
  };

  // 处理快速添加
  const handleQuickAddClick = () => {
    setShowQuickAdd(true);
  };

  const handleQuickAddClose = () => {
    setShowQuickAdd(false);
  };

  const handleTaskCreated = () => {
    setShowQuickAdd(false);
    onTaskCreated?.();
  };

  // 处理跳转到具体日期
  const handleDateJump = () => {
    onDateJump(targetDate);
  };

  // 分离未完成和已完成任务
  const pendingTasks = tasks.filter(task => !task.done);
  const completedTasks = tasks.filter(task => task.done);

  return (
    <div className={`
      border rounded-lg overflow-hidden
      ${isDark ? 'border-ios-dark-gray4 bg-ios-dark-gray6' : 'border-gray-200 bg-white'}
    `}>
      {/* 分组头部 */}
      <div className={`
        px-4 py-3 flex items-center justify-between
        ${isDark ? 'bg-ios-dark-gray5' : 'bg-gray-50'}
      `}>
        <div className="flex items-center space-x-3">
          {/* 分组标题 */}
          <h3 className={`text-lg font-semibold ${isDark ? 'text-ios-dark-gray1' : 'text-gray-900'}`}>
            {getGroupTitle()}
          </h3>
          
          {/* 完成百分比 */}
          <div className={`
            px-2 py-1 rounded-full text-xs font-medium
            ${isDark ? 'bg-ios-dark-gray4 text-ios-dark-gray2' : 'bg-gray-200 text-gray-600'}
          `}>
            {completionStats.completed}/{completionStats.total} ({completionStats.rate}%)
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {/* 快速添加按钮 */}
          <button
            onClick={handleQuickAddClick}
            className={`
              p-1.5 rounded-full transition-colors
              ${isDark 
                ? 'hover:bg-ios-dark-gray4 text-ios-dark-gray2' 
                : 'hover:bg-gray-200 text-gray-600'
              }
            `}
            title={t('actions.addTask')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>

          {/* 跳转按钮 */}
          <button
            onClick={handleDateJump}
            className={`
              p-1.5 rounded-full transition-colors
              ${isDark
                ? 'hover:bg-ios-dark-gray4 text-ios-dark-gray2'
                : 'hover:bg-gray-200 text-gray-600'
              }
            `}
            title={t('actions.jumpToDate')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* QuickAdd组件 */}
      {showQuickAdd && (
        <div className={`border-b ${isDark ? 'border-ios-dark-gray4' : 'border-gray-200'}`}>
          <QuickAddBarV2
            isOpen={showQuickAdd}
            onClose={handleQuickAddClose}
            onTaskCreated={handleTaskCreated}
          />
        </div>
      )}

      {/* 任务列表 */}
      <div className="divide-y divide-gray-200 dark:divide-ios-dark-gray4">
        {/* 未完成任务 */}
        {pendingTasks.map((task) => (
          <div key={task.id}>
            <TaskItemV2
              task={task}
              onToggle={onTaskToggle}
              onClick={onTaskClick}
              viewMode="list"
              showDatePrefix={false}
              isFocused={false}
              isTemporaryVisible={false}
            />
          </div>
        ))}

        {/* 已完成任务 */}
        {completedTasks.map((task) => (
          <div key={task.id}>
            <TaskItemV2
              task={task}
              onToggle={onTaskToggle}
              onClick={onTaskClick}
              viewMode="list"
              showDatePrefix={false}
              isFocused={false}
              isTemporaryVisible={false}
            />
          </div>
        ))}

        {/* 空状态 */}
        {tasks.length === 0 && (
          <div className={`
            px-4 py-6 text-center
            ${isDark ? 'text-ios-dark-gray3' : 'text-gray-500'}
          `}>
            <p className="text-sm">{t('empty.noTasksForDate')}</p>
            <button
              onClick={handleQuickAddClick}
              className={`
                mt-2 text-xs px-3 py-1 rounded-full transition-colors
                ${isDark
                  ? 'text-ios-dark-blue hover:bg-ios-dark-gray4'
                  : 'text-blue-600 hover:bg-blue-50'
                }
              `}
            >
              {t('actions.addTask')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// 使用React.memo优化性能，避免不必要的重新渲染
export const RecentTimeGroup = React.memo(RecentTimeGroupComponent);
